import React, { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AlwaysOnAboutRoomModalTab from './AlwaysOnAboutRoomModalTab'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import zIndex from 'styles/tools/z-index'
import AlwaysOnPackageOptionsModalTab from './AlwaysOnPackageOptionsModalTab'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOnMobileStickyHeaderFilters'

const StickyTabButtonsGroup = styled(TabButtonsGroup)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  room: App.PackageRoomType
  packageOptions: Array<App.OfferPackageOptionView>
  onClose: () => void
}

function AlwaysOnRoomAndPackageDetailsModal(props: Props) {
  const {
    room,
    packageOptions,
    onClose,
  } = props

  const intl = useIntl()
  const [selectedTab, setSelectedTab] = useState<'package-options' | 'about-room'>('package-options')
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const packageViews = packageOptionsByRoom.find(option => option.roomId === room.id)?.packageViews
  const pkg = packageViews?.[0].package as App.HotelPackage

  const selectPackageOptionsTab = useCallback(() => {
    setSelectedTab('package-options')
  }, [])

  const selectAboutRoomTab = useCallback(() => {
    setSelectedTab('about-room')
  }, [])

  if (!pkg || !packageViews?.length) return null

  return (<>
    <ModalHeader
      title={room.name}
      onCloseButtonClick={onClose}
    >
      <AlwaysOnMobileStickyHeaderFilters />
    </ModalHeader>
    <ModalBody>
      <AccommodationRoomTile roomPackage={pkg}>
        <AccommodationRoomTileImage roomPackage={pkg} />
      </AccommodationRoomTile>
      <StickyTabButtonsGroup fit="grow">
        <TabButton size="medium" isActive={selectedTab === 'package-options'} onClick={selectPackageOptionsTab}>
          {intl.formatMessage({
            id: 'JZ1DBVpUXIQTmvpQk8nR',
            defaultMessage: 'Package options',
            description: 'Tab label for the package options section in the room details modal',
          })}
        </TabButton>
        <TabButton size="medium" isActive={selectedTab === 'about-room'} onClick={selectAboutRoomTab}>
          {intl.formatMessage({
            id: 'xs3dmCH04O4zbfIsUwza',
            defaultMessage: 'About the room',
            description: 'Tab label for the room details section in the room details modal',
          })}
        </TabButton>
      </StickyTabButtonsGroup>
      {selectedTab === 'package-options' && <AlwaysOnPackageOptionsModalTab packageViews={packageViews} />}
      {selectedTab === 'about-room' && <AlwaysOnAboutRoomModalTab pkg={pkg} />}
    </ModalBody>
  </>)
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  packageOptions: getDurationPackageOptions(state, appState),
}), AlwaysOnRoomAndPackageDetailsModal)
